import React, { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { TreeSelect, TreeSelectOptionType } from '@bringg/react-components';
import { useMonitoringTranslations } from '@bringg-frontend/monitoring';

import { TeamsTree, useTeamsTreeData } from 'bringg-web/hooks';
import { useStores } from 'bringg-web/recipes';
import { runTeamNameTranslations } from 'bringg-web/features/runs/runs-filter/runs-filter.consts';
import TeamCustomTag from './components/team-custom-tag/team-custom-tag';
import { noTeamOption } from './components/no-team-option/no-team-option';

export const MAX_TEAM_LIMIT = 15;

const getSelectedParents = (
	treeData: TeamsTree[],
	selected: number[] = [],
	selectedParents = new Set<number>(),
	parentId?: number
) => {
	for (const data of treeData) {
		if (selected.includes(data.value)) {
			selectedParents.add(parentId || data.value);
		} else if (data.children?.length) {
			getSelectedParents(data.children, selected, selectedParents, parentId || data.value);
		}
	}

	return selectedParents;
};

interface Props {
	selectedTeams?: number[];
	onTeamsSelect: (teamIds: number[]) => void;
}

const TeamSelector = ({ selectedTeams, onTeamsSelect }: Props) => {
	const { t } = useTranslation();
	const { teamLimitInfo, selectAllTeams } = useMonitoringTranslations();
	const {
		teamsStore,
		merchantConfigurationsStore: { configuration },
		usersStore
	} = useStores();

	const allowNoTeamSelection =
		usersStore.isAdmin || !configuration.enable_teams || configuration.allow_dispatcher_to_access_all_teams;
	const teamOptionsTree = useTeamsTreeData(teamsStore.all);
	const teamOptions = useMemo(() => {
		allowNoTeamSelection && (teamOptionsTree as TreeSelectOptionType[]).unshift(noTeamOption);

		return teamOptionsTree;
	}, [teamOptionsTree, allowNoTeamSelection]);
	const [treeData, setTreeData] = useState(teamOptions);
	const [showMessage, setShowMessage] = useState(false);

	const allowSelectAll = teamOptions.length < MAX_TEAM_LIMIT;

	useEffect(() => {
		if (!allowSelectAll) {
			const selectedParents = getSelectedParents(teamOptions, selectedTeams);

			if (selectedParents.size >= MAX_TEAM_LIMIT) {
				const treeDataWithDisabledItems = teamOptions.map(data => {
					if (!selectedParents.has(data.value)) {
						return {
							...data,
							disabled: true,
							children: (data.children || []).map(child => ({ ...child, disabled: true }))
						};
					}

					return data;
				});

				setTreeData(treeDataWithDisabledItems);
				setShowMessage(true);
			} else {
				setTreeData(teamOptions);
				setShowMessage(false);
			}
		}
	}, [selectedTeams]);

	return (
		<TreeSelect
			data-test-id="monitoring-teams-selector"
			placeholder={runTeamNameTranslations(t).teamName}
			treeData={treeData}
			multiple
			allowSelectAll={allowSelectAll}
			value={selectedTeams}
			onChange={teamIds => {
				onTeamsSelect(teamIds as number[]);
			}}
			selectAllText={selectAllTeams}
			infoMessage={showMessage ? teamLimitInfo : undefined}
			status={!selectedTeams?.length ? 'error' : undefined}
			tagRender={TeamCustomTag}
			allowClear
		/>
	);
};

export default TeamSelector;
